import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Sparkles, Loader2, Save, Trash2, RefreshCcw, History, Star, Archive, Copy as CopyIcon, Send, MoreHorizontal } from "lucide-react";
import { toast } from "sonner";

/**
 * useForgeModule — shared hook providing the generate / save / history lifecycle
 * for every Forge tab. The component consuming this drives its own inputs/output UI.
 */
export function useForgeModule(moduleName) {
  const [inputs, setInputs] = useState({});
  const [output, setOutput] = useState(null);
  const [loading, setLoading] = useState(false);
  const [drafts, setDrafts] = useState([]);

  const loadDrafts = async () => {
    try {
      const r = await api.get(`/forge/drafts?module=${moduleName}&limit=10`);
      setDrafts(r.data || []);
    } catch {}
  };

  useEffect(() => { loadDrafts(); }, [moduleName]); // eslint-disable-line

  const generate = async () => {
    setLoading(true);
    try {
      const r = await api.post("/forge/generate", { module: moduleName, inputs });
      setOutput(r.data.output);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed — try again");
    }
    setLoading(false);
  };

  const saveDraft = async (title) => {
    if (!output) return;
    try {
      await api.post("/forge/drafts", { module: moduleName, title, inputs, output });
      toast.success("Saved to drafts");
      loadDrafts();
    } catch {
      toast.error("Could not save");
    }
  };

  const deleteDraft = async (id) => {
    try {
      await api.delete(`/forge/drafts/${id}`);
      loadDrafts();
    } catch {}
  };

  const toggleFavorite = async (d) => {
    try {
      await api.patch(`/forge/drafts/${d.id}`, { is_favorite: !d.is_favorite });
      loadDrafts();
    } catch {}
  };

  const archiveDraft = async (id) => {
    try {
      await api.patch(`/forge/drafts/${id}`, { is_archived: true });
      toast.success("Archived");
      loadDrafts();
    } catch {}
  };

  const duplicateDraft = async (id) => {
    try {
      await api.post(`/forge/drafts/${id}/duplicate`);
      toast.success("Duplicated");
      loadDrafts();
    } catch {
      toast.error("Could not duplicate");
    }
  };

  const promoteDraft = async (id, target) => {
    try {
      const r = await api.post(`/forge/drafts/${id}/${target}`);
      if (target === "to-asset") toast.success(`Asset created · ${r.data?.title || ""}`);
      else toast.success(`Landing page draft · /${r.data?.slug || ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not promote");
    }
  };

  const loadDraft = (d) => {
    setInputs(d.inputs || {});
    setOutput(d.output || null);
  };

  return {
    inputs, setInputs, output, setOutput, loading, generate, drafts,
    saveDraft, deleteDraft, loadDraft,
    toggleFavorite, archiveDraft, duplicateDraft, promoteDraft,
  };
}

export function GenerateButton({ onClick, loading, label = "Generate", testid }) {
  return (
    <button onClick={onClick} disabled={loading} data-testid={testid}
      className="w-full rounded-full ashra-gradient-bg px-4 py-3 text-sm font-semibold text-white inline-flex items-center justify-center gap-2 shadow-sm hover:shadow-md disabled:opacity-60 transition focus-ring">
      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
      {loading ? "Generating…" : label}
    </button>
  );
}

export function ActionRow({ onRegenerate, onSave, regenerating, testid }) {
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={testid}>
      <button onClick={onRegenerate} disabled={regenerating} data-testid={testid ? `${testid}-regen` : undefined}
        className="rounded-full border border-line bg-white px-3 py-1.5 text-xs font-semibold text-ink-soft hover:bg-zinc-50 inline-flex items-center gap-1 disabled:opacity-60 focus-ring">
        <RefreshCcw className={`h-3 w-3 ${regenerating ? "animate-spin" : ""}`} /> Regenerate
      </button>
      <button onClick={onSave} data-testid={testid ? `${testid}-save` : undefined}
        className="rounded-full ashra-gradient-bg px-3 py-1.5 text-xs font-semibold text-white inline-flex items-center gap-1 shadow-sm focus-ring">
        <Save className="h-3 w-3" /> Save draft
      </button>
    </div>
  );
}

export function DraftsList({ drafts, onLoad, onDelete, onToggleFavorite, onArchive, onDuplicate, onPromote, module, testid = "forge-drafts" }) {
  return (
    <div data-testid={testid}>
      <p className="text-[11px] uppercase tracking-wider text-meta font-bold mb-2 inline-flex items-center gap-1.5">
        <History className="h-3 w-3" /> Recent drafts
      </p>
      {drafts.length === 0 ? (
        <p className="text-xs text-meta-soft">Your saved outputs will appear here.</p>
      ) : (
        <ul className="space-y-1.5">
          {drafts.map(d => (
            <li key={d.id} className="group rounded-xl border border-line bg-zinc-50 hover:bg-white hover:border-violet-200 transition p-2.5 flex items-start gap-2" data-testid={`draft-${d.id}`}>
              <button onClick={() => onLoad(d)} className="flex-1 min-w-0 text-left" data-testid={`draft-load-${d.id}`}>
                <p className="text-xs font-semibold text-ink truncate">{d.title}</p>
                <p className="text-[10px] text-meta-soft mt-0.5">{(d.created_at || "").slice(0, 16).replace("T", " ")}</p>
              </button>
              <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition shrink-0">
                {onToggleFavorite && (
                  <button onClick={() => onToggleFavorite(d)} title={d.is_favorite ? "Unstar" : "Star"} data-testid={`draft-fav-${d.id}`}
                    className={`p-1 rounded-md transition ${d.is_favorite ? "text-warning" : "text-meta hover:text-warning"}`}>
                    <Star className={`h-3.5 w-3.5 ${d.is_favorite ? "fill-current" : ""}`} />
                  </button>
                )}
                {onDuplicate && (
                  <button onClick={() => onDuplicate(d.id)} title="Duplicate" data-testid={`draft-dup-${d.id}`} className="p-1 rounded-md text-meta hover:text-ai">
                    <CopyIcon className="h-3.5 w-3.5" />
                  </button>
                )}
                {onPromote && (module === "offer" || module === "content") && (
                  <button onClick={() => onPromote(d.id, "to-asset")} title="Send to Revenue Assets" data-testid={`draft-promote-asset-${d.id}`} className="p-1 rounded-md text-meta hover:text-ai">
                    <Send className="h-3.5 w-3.5" />
                  </button>
                )}
                {onPromote && module === "sales" && (
                  <button onClick={() => onPromote(d.id, "to-landing")} title="Send to Landing Page" data-testid={`draft-promote-landing-${d.id}`} className="p-1 rounded-md text-meta hover:text-ai">
                    <Send className="h-3.5 w-3.5" />
                  </button>
                )}
                {onArchive && (
                  <button onClick={() => onArchive(d.id)} title="Archive" data-testid={`draft-arch-${d.id}`} className="p-1 rounded-md text-meta hover:text-ink">
                    <Archive className="h-3.5 w-3.5" />
                  </button>
                )}
                <button onClick={() => onDelete(d.id)} title="Delete" data-testid={`draft-del-${d.id}`} className="p-1 rounded-md text-meta hover:text-danger">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
